import { selectStubUrl } from '../../selectors/app';
import { selectUser } from '../../selectors/auth';
import { selectUserId } from '../../selectors/auth';
import { showNotification } from '../app/actionCreators';
import { AppErrorCode, NotificationStatus } from '../app/actionTypes';
import {
    nextcloudApi,
    ShareType,
    SharePermissions,
    ApiResponse,
    ResponseError,
} from '@sendent/nextcloud-api';
import { ThunkAction } from 'redux-thunk';
import { selectTranslate } from '../../selectors/ui';
import { AnyAction } from 'redux';
import { RootState } from '../..';
import { setCircles, setShares } from './actionCreators';
import {
    selectDefaultFolderPath,
    selectFormDefaultFolderCircle,
    selectFormDefaultFolderPath,
} from '../../selectors/defaultFolder';
import { Circle } from '../../../services/microsoft/msGraphApi';
import {
    ShareWithCirclesDialogResult,
    submitDialogResult,
} from '../../../services/dialogResults';
import { ServerFile } from '../../../model';
import { logger } from '../../../helpers/logger';

export const fetchCirclesThunk = (): ThunkAction<
    Promise<ApiResponse | ResponseError | null>,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState) {
        const state = getState();
        const stubUrl = selectStubUrl(state);
        const user = selectUser(state);
        if (!stubUrl || !user) return null;
        const response = await nextcloudApi.getCircles(stubUrl, user);
        if (response && response.success) {
            const circles = response.result.ocs.data.map((circle: Circle) => ({
                id: circle.id,
                name: circle.name,
            }));
            dispatch(setCircles(circles));
        }
        return response;
    };
};

export const shareSelectedFolderWithCircleThunk = (): ThunkAction<
    Promise<void>,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const stubUrl = selectStubUrl(state);
        const user = selectUser(state);
        const userId = selectUserId(state);
        const translate = selectTranslate(state);

        const selectedFolderPath = selectFormDefaultFolderPath(state);
        const selectedCircle = selectFormDefaultFolderCircle(state);

        if (!stubUrl || !user || !userId || !selectedCircle) return;
        if (!selectedFolderPath) {
            dispatch(
                showNotification({
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
            return;
        }

        const shareRes = await nextcloudApi.shareByPath({
            requestConfig: { stubUrl, user },
            shareTargetPath: selectedFolderPath,
            shareType: ShareType.Circle,
            shareWith: selectedCircle.id,
            permissions: SharePermissions.All,
        });

        if (!shareRes.success) {
            dispatch(
                showNotification({
                    message: translate('share_with_teams_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
            return;
        }

        dispatch(
            showNotification({
                message: translate('creating_share'),
                status: NotificationStatus.Success,
            }),
        );
        return;
    };
};

export const shareSelectedFileWithCircleThunk = (
    file: ServerFile,
    selectedCircle: Circle,
): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const stubUrl = selectStubUrl(state);
        const user = selectUser(state);
        const userId = selectUserId(state);
        const translate = selectTranslate(state);

        if (!stubUrl || !user || !userId) return;

        if (selectedCircle) {
            const shareRes = await nextcloudApi.shareByPath({
                requestConfig: { stubUrl, user },
                shareTargetPath: file.path,
                shareType: ShareType.Circle,
                shareWith: selectedCircle.id,
                permissions: SharePermissions.Read,
            });

            if (!shareRes.success) {
                dispatch(
                    showNotification({
                        message: translate('share_with_teams_error'),
                        status: NotificationStatus.Error,
                        errorCode: AppErrorCode.CustomError,
                    }),
                );
                return;
            }
            dispatch(
                showNotification({
                    message: translate('creating_share'),
                    status: NotificationStatus.Success,
                }),
            );
        }

        const url = `${user.server}/index.php/f/${file.fileId}`;

        submitDialogResult(
            new ShareWithCirclesDialogResult({
                sharedFileInfoArr: [{ name: file.name, url }],
            }),
        );
    };
};

export const fetchFolderSharesThunk = (): ThunkAction<
    Promise<ApiResponse | ResponseError | null>,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState) {
        const state = getState();
        const stubUrl = selectStubUrl(state);
        const user = selectUser(state);
        const translate = selectTranslate(state);
        const defaultFolderPath = selectDefaultFolderPath(state);

        if (!stubUrl || !user || !defaultFolderPath) {
            logger.error(
                // eslint-disable-next-line max-len
                `Error getting stubUrl or user: stubUrl=${stubUrl}, user=${user}`,
                'Share',
            );
            return null;
        }

        try {
            const response = await nextcloudApi.getShares(
                stubUrl,
                user,
                defaultFolderPath,
            );

            if (response && response.success) {
                const shares = response.result.ocs.data;
                dispatch(setShares(shares));
            } else {
                dispatch(
                    showNotification({
                        message: translate('something_went_wrong'),
                        status: NotificationStatus.Error,
                        errorCode: AppErrorCode.CustomError,
                    }),
                );
            }

            return response;
        } catch (error) {
            logger.error(
                `Error fetching shares: ${
                    error instanceof Error ? error.message : 'unknown'
                }`,
                'Share',
            );
            dispatch(
                showNotification({
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
            return null;
        }
    };
};
